import { getGraphToken } from "./TokenService";

// Storage for notification history per email
const emailNotificationHistory = new Map();
const NOTIFICATION_HISTORY_KEY = 'outlookAddinNotificationHistory';

// Load notification history from localStorage
function loadNotificationHistoryFromStorage() {
  try {
    const stored = localStorage.getItem(NOTIFICATION_HISTORY_KEY);
    if (stored) {
      const parsedHistory = JSON.parse(stored);
      Object.entries(parsedHistory).forEach(([emailId, messages]) => {
        emailNotificationHistory.set(emailId, messages);
      });
    }
  } catch (error) {
    console.error('Error loading notification history from storage:', error);
  }
}

// Save notification history to localStorage
function saveNotificationHistoryToStorage() {
  try {
    const historyObject = {};
    emailNotificationHistory.forEach((messages, emailId) => {
      historyObject[emailId] = messages;
    });
    localStorage.setItem(NOTIFICATION_HISTORY_KEY, JSON.stringify(historyObject));
  } catch (error) {
    console.error('Error saving notification history to storage:', error);
  }
}

// Initialize history from storage
loadNotificationHistoryFromStorage();

// Get or create notification history for an email
function getNotificationHistory(emailId) {
  if (!emailNotificationHistory.has(emailId)) {
    emailNotificationHistory.set(emailId, []);
  }
  return emailNotificationHistory.get(emailId);
}

// Get current email ID for tracking
function getCurrentEmailId() {
  try {
    if (Office?.context?.mailbox?.item?.itemId) {
      const itemId = Office.context.mailbox.item.itemId;
      let consistentId = itemId;
      
      try {
        if (Office.context.mailbox.convertToRestId) {
          consistentId = Office.context.mailbox.convertToRestId(
            itemId,
            Office.MailboxEnums.RestVersion.v2_0
          );
        }
      } catch (conversionError) {
        // Use raw ID if conversion fails
      }
      
      return consistentId;
    }
  } catch (error) {
    console.error("Error getting email ID:", error);
  }
  return null;
}

// Green checkmark icon for success notifications
const GREEN_CHECKMARK_ICON = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAABDElEQVR4nO2WTQrCMBCF07oUPYHoDQRJqOAhBDN0IdqTuMgxXPmDiomuPIEgnkKoRxAP4KIyXWgoKqIxouTBQFb93oT3SghxcnL6WyXEYwvo4uDZOp9K3mMKknQkj+zC51CnEk5fMVCddPNUwU6Db8NlmLNmgEk+vsLhWJvxij244nCBK0joAtrW4MG0VaKSHy5wBSNrcCKEzySsNfg+mHaK1npMtcph+rEFJsIUXT/KB7jlM5VDM2/DswbumWismgWmINa235irnBA+Qh+ZyFaOLsMyMaqEeEzyfsbEMA2dtcqJGzehQOHG2t9u/Bn4AxPaxJgF8nGJW5kwVLlXTRir3E89NJycnIhhnQEHiOlyTZ5aVgAAAABJRU5ErkJggg==';

// Safely add notification banner to email
export function addEmailNotificationSafely(notificationId, notificationConfig, callback) {
  if (!Office?.context?.mailbox?.item?.notificationMessages) {
    console.error("Notification messages API not available");
    callback && callback({ status: 'failed', error: 'API not available' });
    return;
  }

  const finalNotificationId = notificationId || `notification_${Date.now()}`;
  
  // Remove any existing notification with the same key, then add new one
  Office.context.mailbox.item.notificationMessages.removeAsync(
    finalNotificationId,
    function(removeResult) {
      Office.context.mailbox.item.notificationMessages.addAsync(
        finalNotificationId,
        notificationConfig,
        function(addResult) {
          if (addResult.status === Office.AsyncResultStatus.Succeeded) {
            callback && callback({ status: 'success', notificationId: finalNotificationId });
          } else if (addResult.error.name === 'DuplicateNotificationKey') {
            // Try with unique timestamp if still getting duplicate key error
            const uniqueId = `${finalNotificationId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            Office.context.mailbox.item.notificationMessages.addAsync(
              uniqueId,
              notificationConfig,
              function(retryResult) {
                if (retryResult.status === Office.AsyncResultStatus.Succeeded) {
                  callback && callback({ status: 'success', notificationId: uniqueId });
                } else {
                  console.error("Failed to add notification even with unique ID:", retryResult.error);
                  callback && callback({ status: 'failed', error: retryResult.error });
                }
              }
            );
          } else {
            console.error("Error adding notification to email:", addResult.error);
            callback && callback({ status: 'failed', error: addResult.error });
          }
        }
      );
    }
  );
}

export async function markEmailAsCompleteNew(notificationId, messageText, icon, persistent) {
  try {
    if (!Office?.context?.mailbox?.item) {
      console.error("Office context not available");
      return;
    }

    // Get the REST ID for Graph API compatibility
    let restId;
    try {
      if (Office.context.mailbox.convertToRestId) {
        restId = Office.context.mailbox.convertToRestId(
          Office.context.mailbox.item.itemId,
          Office.MailboxEnums.RestVersion.v2_0
        );
      } else {
        restId = Office.context.mailbox.item.itemId;
      }
    } catch (idError) {
      console.error("Error getting REST ID:", idError);
      restId = Office.context.mailbox.item.itemId;
    }

    if (!restId) {
      console.error("Email ID not available");
      return;
    }

    // Get access token and set flag to complete
    const accessToken = await getGraphToken();
    
    try {
      const flagPayload = {
        flag: {
          flagStatus: "complete",
          completedDateTime: {
            dateTime: new Date().toISOString(),
            timeZone: "UTC"
          }
        }
      };
      
      const flagResponse = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${restId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(flagPayload)
      });

      if (flagResponse.ok) {
        console.log("Email flag set to complete successfully");
      } else {
        console.error("Error setting flag via Graph API:", flagResponse.status);
        
        // Fallback to Office.js
        try {
          if (Office.context.mailbox.item.setAsync) {
            Office.context.mailbox.item.setAsync(
              { flag: { flagStatus: Office.MailboxEnums.FlagStatus.Complete } },
              function(result) {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                  console.error("Office.js flag fallback failed:", result.error);
                }
              }
            );
          }
        } catch (fallbackError) {
          console.error("Office.js flag fallback error:", fallbackError);
        }
      }
    } catch (flagError) {
      console.error("Error with Graph API flag request:", flagError);
    }

    // Show completion banner
    try {
      const bannerMessage = messageText || 'Action completed';
      addMultipleNotificationBanners(
        bannerMessage,
        icon || GREEN_CHECKMARK_ICON,
        persistent !== false,
        function(result) {
          if (result.status !== 'success') {
            console.error("Failed to add completion banner:", result.error);
          }
        }
      );
    } catch (bannerError) {
      console.error("Error adding banner:", bannerError);
    }

  } catch (error) {
    console.error("Error marking email as complete:", error);
  }
}



export async function getEmailCompletionStatus() {
  try {
    const accessToken = await getGraphToken();  
    const restId = Office.context.mailbox.convertToRestId(
      Office.context.mailbox.item.itemId,
      Office.MailboxEnums.RestVersion.v2_0
    );
    const response = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${restId}`, {
      method: 'GET',
      headers: { 
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      console.error("Error fetching email completion status:", response.statusText);
      throw new Error("Failed to fetch email completion status");
    }
  } catch (error) {
    console.error("Error with Graph API request:", error);
    throw error;
  }
}



export function sendEmail(subject, body, toRecipient, attachments = [], customHeaders = {}, ccRecipient) {
  return getGraphToken().then(accessToken => {
    const buildEmail = () => {
      const email = {
        message: {
          subject,
          importance: "normal",
          body: { contentType: "HTML", content: body },
          toRecipients: [
            { emailAddress: { address: toRecipient } }
          ],
          ccRecipients: Array.isArray(ccRecipient)
          ? ccRecipient.map(email => ({ emailAddress: { address: email } }))
          : ccRecipient
            ? [{ emailAddress: { address: ccRecipient } }]
            : [],
        }
      };

      const filtered = Object.entries(customHeaders).filter(([, v]) => v);
      if (filtered.length) {
        email.message.internetMessageHeaders = filtered.map(([name, value]) => ({
          name,
          value: String(value)
        }));
      }
      
      if (attachments?.length) {
        email.message.attachments = attachments.map((a) => ({
          "@odata.type": "#microsoft.graph.fileAttachment",
          "name": a.name,
          "contentType": a.contentType || "application/octet-stream",
          "contentBytes": a.contentBytes
        }));
      }
      
      return email;
    };

    const email = buildEmail();

    const sendOnce = (token) => {
      return fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(email)
      });
    };

    return sendOnce(accessToken).then(r => {
      if (r.status === 401) {
        // Retry with fresh token on authentication failure
        return getGraphToken(true).then(newToken =>
          sendOnce(newToken).then(r2 => {
            if (!r2.ok) return r2.text().then(t => { throw new Error(`Retry failed: ${r2.status} ${t}`); });
            return r2;
          })
        );
      }
      if (!r.ok) {
        return r.text().then(t => { 
          console.error('Email send failed:', r.status, t);
          throw new Error(`Failed to send email: ${r.status} ${t}`); 
        });
      }
      return r;
    });
  });
}



// Restore notification banners when user switches back to an email
export function restoreNotificationBannersForEmail() {
  const emailId = getCurrentEmailId();
  if (!emailId) {
    return Promise.resolve();
  }

  const notificationHistory = getNotificationHistory(emailId);
  if (!notificationHistory || notificationHistory.length === 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    if (Office?.context?.mailbox?.item?.notificationMessages) {
      Office.context.mailbox.item.notificationMessages.getAllAsync(function(getAllResult) {
        if (getAllResult.status === Office.AsyncResultStatus.Succeeded) {
          const existingNotifications = getAllResult.value;
          
          // Remove all existing notifications
          const clearPromises = existingNotifications.map(notification => {
            return new Promise((clearResolve) => {
              Office.context.mailbox.item.notificationMessages.removeAsync(
                notification.key,
                function(removeResult) {
                  clearResolve();
                }
              );
            });
          });

          // Wait for all removals to complete, then restore
          Promise.all(clearPromises).then(() => {
            // Show each message as a separate notification with delays
            const restorationPromises = notificationHistory.map((message, index) => {
              return new Promise((restoreResolve) => {
                setTimeout(() => {
                  // Extract core message for deterministic ID generation
                  const coreMessage = message.includes('] ') ? message.split('] ')[1] : message;
                  const deterministicId = `msg_${coreMessage.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}_${Math.abs(coreMessage.split('').reduce((hash, char) => hash + char.charCodeAt(0), 0))}`;
                  
                  const notificationConfig = {
                    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
                    message: message,
                    icon: 'Icon.share',
                    persistent: true
                  };

                  Office.context.mailbox.item.notificationMessages.addAsync(
                    deterministicId,
                    notificationConfig,
                    (result) => {
                      restoreResolve();
                    }
                  );
                }, index * 150); // 150ms delay between each notification
              });
            });

            Promise.all(restorationPromises).then(() => {
              resolve();
            });
          });
        } else {
          console.error("Failed to get existing notifications:", getAllResult.error);
          resolve();
        }
      });
    } else {
      console.warn("Notification API not available during restoration");
      resolve();
    }
  });
}

// Add multiple notification banners with proper deduplication
export function addMultipleNotificationBanners(messageText, icon, persistent, callback) {
  if (!Office?.context?.mailbox?.item?.notificationMessages) {
    console.error("Notification messages API not available");
    callback && callback({ status: 'failed', error: 'API not available' });
    return;
  }

  const emailId = getCurrentEmailId();
  if (!emailId) {
    console.error("Could not get email ID for tracking notifications");
    callback && callback({ status: 'failed', error: 'Email ID not available' });
    return;
  }

  const notificationHistory = getNotificationHistory(emailId);
  
  // Create unique key based on email + core message (without timestamp)
  const coreMessage = messageText.replace(/\s+(on\s+\w+.*|at\s+\d+.*)/g, '').trim();
  
  // Check if this exact message already exists
  const existingMessage = notificationHistory.find(msg => {
    const existingCore = msg.includes('] ') ? msg.split('] ')[1] : msg;
    return existingCore.replace(/\s+(on\s+\w+.*|at\s+\d+.*)/g, '').trim() === coreMessage;
  });
  
  if (existingMessage) {
    callback && callback({ 
      status: 'success', 
      notificationId: 'duplicate_skipped',
      messageCount: notificationHistory.length,
      duplicate: true 
    });
    return;
  }
  
  // Add current message to history with timestamp
  const timestamp = new Date().toLocaleString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  const newMessage = `[${timestamp}] ${messageText}`;
  notificationHistory.push(newMessage);

  // Save to localStorage for persistence
  saveNotificationHistoryToStorage();

  // Create a deterministic notification ID based on core message
  const deterministicId = `msg_${coreMessage.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}_${Math.abs(coreMessage.split('').reduce((hash, char) => hash + char.charCodeAt(0), 0))}`;
  
  const notificationConfig = {
    type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
    message: newMessage,
    icon: 'Icon.share',
    persistent: persistent !== false
  };

  // Add this as a separate notification
  Office.context.mailbox.item.notificationMessages.addAsync(
    deterministicId,
    notificationConfig,
    function(addResult) {
      if (addResult.status === Office.AsyncResultStatus.Succeeded) {
        callback && callback({ 
          status: 'success', 
          notificationId: deterministicId,
          messageCount: notificationHistory.length 
        });
      } else {
        console.error("Error adding notification:", addResult.error);
        callback && callback({ status: 'failed', error: addResult.error });
      }
    }
  );
}

// Clear notification history for an email
export function clearEmailNotificationHistory(emailId) {
  if (emailId && emailNotificationHistory.has(emailId)) {
    emailNotificationHistory.delete(emailId);
    saveNotificationHistoryToStorage();
  }
}

// Get notification history for an email
export function getEmailNotificationHistory(emailId) {
  return emailNotificationHistory.get(emailId) || [];
}

// Initialize notification history from existing email notifications  
export function initializeNotificationHistoryFromEmail() {
  const emailId = getCurrentEmailId();
  if (!emailId) {
    return null;
  }

  loadNotificationHistoryFromStorage();
  const existingHistory = getEmailNotificationHistory(emailId);
  return existingHistory;
}



